import { useMemo } from 'react';
import { MultiWebClientMerchantModel } from '@/User/domain';
import { normalize } from '../utils/normalize';

type FilterResult = {
  filteredMerchants: MultiWebClientMerchantModel[];
  expandedParents: Set<number>;
};

/**
 * Hook personalizado para filtrar comercios basándose en el término de búsqueda.
 * Implementa búsqueda insensible a acentos con un mínimo de 3 caracteres.
 * Además, retorna los padres que deben estar expandidos cuando sus hijos coinciden.
 * 
 * @param merchants - Lista completa de comercios
 * @param searchTerm - Término de búsqueda ingresado por el usuario
 * @returns Objeto con la lista filtrada y los padres que deben expandirse
 */
export const useMasterMerchantFilter = (
  merchants: MultiWebClientMerchantModel[],
  searchTerm: string
): FilterResult => {
  return useMemo(() => {
    // Si la búsqueda tiene menos de 3 caracteres, devolver todos los comercios sin expandir
    if (searchTerm.length < 3) {
      return {
        filteredMerchants: merchants,
        expandedParents: new Set<number>()
      };
    }

    const normalizedSearch = normalize(searchTerm);
    const expandedParents = new Set<number>();

    // Función para verificar si la cadena de búsqueda está presente en algún campo
    const matchesSearch = (name: string, cif: string, code: string, isMasterGroup?: boolean) => {
      return normalize(name).includes(normalizedSearch) ||
             !isMasterGroup && normalize(cif).includes(normalizedSearch) ||
             !isMasterGroup && normalize(code.toString()).includes(normalizedSearch);
    };

    // Filtrar comercios y sus sub-items
    const filteredMerchants = merchants
      .map(merchant => {
        // Verificar si el comercio principal coincide
        const matchMain = matchesSearch(
          merchant.name,
          merchant.cif,
          merchant.code.toString(),
          merchant.isMasterGroup
        );

        // Filtrar sub-items que coinciden con la búsqueda
        const matchingSubs = Array.isArray(merchant.subItems)
          ? merchant.subItems.filter(sub =>
              matchesSearch(sub.name, sub.cif, sub.code.toString(), sub.isMasterGroup)
            )
          : [];

        // Si hay sub-items que coinciden pero el padre no, agregar el padre a expandidos
        if (!matchMain && matchingSubs.length > 0) {
          expandedParents.add(merchant.code);
        }

        // Si coincide el principal o hay sub-items que coinciden, incluir el comercio
        if (matchMain || matchingSubs.length > 0) {
          return {
            ...merchant,
            subItems: matchingSubs.length > 0 ? matchingSubs : merchant.subItems
          };
        }

        return undefined;
      })
      .filter((m): m is MultiWebClientMerchantModel => m !== undefined);

    return { filteredMerchants, expandedParents };
  }, [merchants, searchTerm]);
};
